import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { InvoicePermissionHolders, PurchaseDocTab } from "types/InvoiceInbox";
import { TAB_ACTORS, tabActorLabelKey } from "./invoice-inbox-tabs";

/**
 * "Who can move this queue" — one line per permission behind the SELECTED
 * tab's action, above the register's tabs content. Exists because a tab
 * nobody can act on is a silent dead end: the person who set the module up
 * is often the only Master, and nothing else in the register would ever say
 * a queue has no one to move it — see TAB_ACTORS for which permission(s)
 * gate each tab. The server lists EXPLICIT holders only (a Master group by
 * itself names nobody), so "nobody holds it" is shown exactly when the
 * permission is assigned to no one — the viewer's own Master status only
 * decides whether the "assign it" link is offered.
 */

type Props = {
  tab: PurchaseDocTab;
  data: InvoicePermissionHolders | null;
  companyId: string;
  isMaster: boolean;
};

export default function InvoiceInboxActors({ tab, data, companyId, isMaster }: Props) {
  const { t } = useTranslation();
  const keys = TAB_ACTORS[tab] || [];
  if (!data || !keys.length) return null;

  // Only a Master can open the permissions screen, so the "go fix it"
  // link is only worth offering to one — everyone else just sees the warning.
  const assignLink = (
    <Link to={`/app/${companyId}/settings/permissions`} className="ml-1 text-red-700 underline">
      {t("invoice_inbox_actors_assign", { defaultValue: "Give someone this right in permissions" })}
    </Link>
  );

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm" data-testid="invoice-inbox-actors">
      {keys.map((key) => {
        const holders = data.holders[key] || [];
        const permission = data.permissions[key] || key;
        return (
          <div key={key} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-0.5">
            <span className="text-gray-500">{t(tabActorLabelKey(tab, key))}:</span>
            {holders.length === 0 ? (
              <span className="inline-flex items-center gap-1 text-red-700">
                <AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
                {t("invoice_inbox_actors_nobody", {
                  defaultValue: 'Nobody holds the permission "{{permission}}"',
                  permission,
                })}
                {isMaster && assignLink}
              </span>
            ) : (
              <>
                {holders.map((holder) => (
                  <span
                    key={holder.id}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-gray-800"
                  >
                    {holder.name}
                    {!holder.isActivated && (
                      <span className="text-xs text-amber-700">
                        ({t("invoice_inbox_actors_not_activated", { defaultValue: "not activated" })})
                      </span>
                    )}
                  </span>
                ))}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
